import { useIsDarkMode } from '@shared/lib/hooks/useIsDarkMode';

import { FadeIn, ScaleFadeIn } from './Animations';
import { cn } from './cn';

/**
 * Resolve CSS variable colors to actual hex values for contrast calculation.
 */
const resolveCssVarColor = (color: string): string => {
  if (color?.startsWith('var(')) {
    const varName = color
      .replace(/^var\(/, '')
      .replace(/\)$/, '')
      .split(',')[0]
      .trim();
    const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    return resolved || '#6B7280';
  }
  return color || '#6B7280';
};

/**
 * Simple luminance check to determine if a color is dark.
 */
const isColorDark = (hex: string): boolean => {
  const clean = hex.replace('#', '');
  if (clean.length < 6) return false;
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
};

export interface StatusTagProps {
  title: string;
  color: string;
  className?: string;
}

export const StatusTag = ({ title, color, className }: StatusTagProps) => {
  // The CSS variable behind `color` flips with the `dark` class on <html>, but its value is
  // resolved to a literal below. Subscribe to theme toggles so the contrast-based text color
  // is recomputed from the current theme instead of staying stale until the next re-render.
  useIsDarkMode();
  const resolvedColor = resolveCssVarColor(color);
  const textColor = isColorDark(resolvedColor) ? 'var(--h2o-gray200, #E5E7EB)' : 'var(--h2o-gray900, #111827)';

  return (
    <ScaleFadeIn
      className={cn(
        'inline-flex items-center justify-center rounded-full px-3 py-0.5 text-xs font-medium leading-5 whitespace-nowrap',
        className
      )}
      style={{ backgroundColor: color, color: textColor, minWidth: 80 }}
    >
      {title}
    </ScaleFadeIn>
  );
};

export interface MinimalStatusTagProps {
  title: string;
  color: string;
  className?: string;
}

export const MinimalStatusTag = ({ title, color, className }: MinimalStatusTagProps) => (
  <FadeIn className={cn('flex items-center gap-1.5 text-sm text-gray-900', className)} transition={{ duration: 0.2 }}>
    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} title={title} />
    <span className="font-normal">{title}</span>
  </FadeIn>
);
